/**
 * @file reportCatalog.ts
 * @brief Company Admin report catalog metadata (Phase B P0 + backlog stubs).
 *
 * @references
 * - docs/product/reporting.md
 *
 * @author DES
 * @date 27-07-2026
 */

import {
  CalendarDays,
  Coins,
  Gauge,
  Languages,
  LayoutGrid,
  type LucideIcon,
  MicVocal,
  UserCheck,
  Users,
} from "lucide-react";
import { BoardroomTable } from "@/components/icons/BoardroomTable";

export type CompanyReportId =
  | "co-usage-employee"
  | "co-spend-invoices"
  | "co-meeting-intel"
  | "co-pool-burn"
  | "co-product-mix"
  | "co-language-pairs"
  | "co-schedule-ops"
  | "co-enrollment"
  | "co-adoption";

export type CompanyReportMeta = {
  id: CompanyReportId;
  title: string;
  description: string;
  shape: "commercial" | "adoption" | "operational" | "insight";
  priority: "P0" | "P1" | "P2";
  available: boolean;
  icon: LucideIcon;
};

export const COMPANY_REPORT_CATALOG: CompanyReportMeta[] = [
  {
    id: "co-usage-employee",
    title: "Usage by employee",
    description:
      "Interpretation, Meeting Intel, and Notes volume per team member for the selected range.",
    shape: "adoption",
    priority: "P0",
    available: true,
    icon: Users,
  },
  {
    id: "co-spend-invoices",
    title: "Spend by product",
    description:
      "Company spend broken down by Interpretation, Meeting Intel, Notes, and Plan seats.",
    shape: "commercial",
    priority: "P0",
    available: true,
    icon: Coins,
  },
  {
    id: "co-meeting-intel",
    title: "Meeting Intel summary",
    description:
      "Meeting count, duration, engagement/sentiment averages, and action-item volume.",
    shape: "insight",
    priority: "P0",
    available: true,
    icon: BoardroomTable, // DES 08-10-2026: app icon, was Presentation
  },
  {
    id: "co-pool-burn",
    title: "Seat & pool burn",
    description:
      "Enterprise shared pool used vs remaining, seats assigned, and seated employees.",
    shape: "commercial",
    priority: "P0",
    available: true,
    icon: Gauge,
  },
  {
    id: "co-product-mix",
    title: "Product mix",
    description:
      "Item counts, time, and spend share across Interpretation, Meeting Intel, Notes, and Plan seats.",
    shape: "insight",
    priority: "P1",
    available: true,
    icon: LayoutGrid,
  },
  {
    id: "co-language-pairs",
    title: "Language-pair usage",
    description: "Top interpretation language pairs for your company.",
    shape: "insight",
    priority: "P1",
    available: true,
    icon: Languages,
  },
  {
    id: "co-schedule-ops",
    title: "Schedule ops",
    description:
      "Booked vs cancelled diary sessions and reschedule approve/decline rates.",
    shape: "operational",
    priority: "P2",
    available: true,
    icon: CalendarDays,
  },
  {
    id: "co-enrollment",
    title: "Enrollment readiness",
    description: "Employees with or without voice enrollment.",
    shape: "adoption",
    priority: "P2",
    available: true,
    icon: MicVocal,
  },
  {
    id: "co-adoption",
    title: "Feature adoption",
    description: "Active vs idle employees against company feature toggles.",
    shape: "adoption",
    priority: "P2",
    available: true,
    icon: UserCheck,
  },
];

export function getCompanyReport(
  id: string | undefined,
): CompanyReportMeta | undefined {
  if (!id) return undefined;
  return COMPANY_REPORT_CATALOG.find((r) => r.id === id);
}
